import React, { Fragment, useEffect, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';

import aspirantesHelper from '../../Helpers/Aspirantes';

const ASPIRANTE_ID = '63215a3afa07afe88e8c884f';

const secciones = [
    {
        titulo: 'Fechas',
        campos: [
            { campo: 'inicio_cine_animacion', etiqueta: 'Inicio de Cine y Animación' },
            { campo: 'siguiente_inicio_cine_animacion', etiqueta: 'Siguiente inicio de Cine y Animación' },
            { campo: 'siguiente_inicio_marketing', etiqueta: 'Siguiente inicio de Marketing Digital', ayuda: 'Déjalo vacío para ocultarlo', opcional: true },
            { campo: 'dia_limite_colegiatura', etiqueta: 'Día límite de pago de colegiatura', numero: true, min: 1, max: 31 }
        ]
    },
    {
        titulo: 'Cine, Animación y Multimedia',
        campos: [
            { campo: 'cine_inscripcion', etiqueta: 'Inscripción', numero: true },
            { campo: 'cine_colegiatura', etiqueta: 'Colegiatura antes del día límite', numero: true },
            { campo: 'cine_colegiatura_tardia', etiqueta: 'Colegiatura después del día límite', numero: true },
            { campo: 'cine_total_colegiaturas', etiqueta: 'Total de las 4 colegiaturas', numero: true },
            { campo: 'cine_total_contado', etiqueta: 'Total de contado', numero: true }
        ]
    },
    {
        titulo: 'Marketing Digital',
        campos: [
            { campo: 'marketing_inscripcion', etiqueta: 'Inscripción', numero: true },
            { campo: 'marketing_colegiatura', etiqueta: 'Colegiatura antes del día límite', numero: true },
            { campo: 'marketing_colegiatura_tardia', etiqueta: 'Colegiatura después del día límite', numero: true },
            { campo: 'marketing_total_colegiaturas', etiqueta: 'Total de las 4 colegiaturas', numero: true },
            { campo: 'marketing_total_contado', etiqueta: 'Total de contado', numero: true }
        ]
    },
    {
        titulo: 'Otros',
        campos: [
            { campo: 'calendario_vigente', etiqueta: 'Calendario vigente de los precios' },
            { campo: 'dias_pago_contado', etiqueta: 'Días naturales para pagar de contado', numero: true },
            { campo: 'costo_extraordinario', etiqueta: 'Costo de examen extraordinario', numero: true }
        ]
    }
];

const Aspirantes = props => {
    const { editar, setEditar, recargar } = useAuth();

    const [aspirante, setAspirante] = useState(null);
    const [seccionActiva, setSeccionActiva] = useState(null);

    useEffect(() => {
        obtenerDatos();
    }, []);

    const obtenerDatos = async () => {
        const res = await aspirantesHelper.obtenerAspirantes(ASPIRANTE_ID);
        if (res.success) {
            setAspirante(res.data);
            setEditar(res.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener las fechas y costos, codigo: ' + res.code, 'error');
        }
    }

    // Al cerrar el panel se limpia `editar`; entonces el iframe vuelve a mostrar lo guardado
    const borrador = editar._id ? editar : aspirante;
    const hayCambios = Boolean(editar._id && aspirante) && JSON.stringify(editar) !== JSON.stringify(aspirante);

    const panel = usePanelEdicion({ hayCambios, onCerrar: () => setEditar({}) });

    const abrirPanel = () => {
        if (!editar._id && aspirante) setEditar(aspirante);
        panel.abrir('editar');
    }

    const descartar = async () => {
        if (await confirmarDescartar()) setEditar(aspirante);
    }

    const seleccionarCampo = ({ id }) => {
        abrirPanel();
        document.getElementById(id)?.focus();
    }

    const { iframeRef, src } = usePreviewSitio({
        page: 'inscripcion',
        ruta: '/proceso-admision',
        data: borrador,
        onSelect: seleccionarCampo
    });

    const leerDato = e => {
        const { name, value, type } = e.target;
        setEditar(editar => ({ ...editar, [name]: type === 'number' && value !== '' ? Number(value) : value }));
    }

    const actualizar = async e => {
        e.preventDefault();
        const faltantes = secciones.flatMap(seccion => seccion.campos).filter(({ campo, opcional }) => !opcional && editar[campo] === '');
        if (faltantes.length) return Swal.fire('Error!', 'Faltan datos por llenar: ' + faltantes.map(({ etiqueta }) => etiqueta).join(', '), 'error');
        if ((await aspirantesHelper.editarAspirantes(editar, ASPIRANTE_ID))?.success) recargar();
    }

    return (
        <Fragment>
            <SideNav />
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar fechas y costos'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    secciones={secciones.map((seccion, i) => ({ id: String(i), titulo: seccion.titulo }))}
                    seccionActiva={seccionActiva}
                    onSeccion={setSeccionActiva}
                    hayCambios={hayCambios}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={descartar} disabled={!hayCambios}>
                                Descartar
                            </button>
                            <button type='submit' form='form_inscripcion' className='boton_admin' disabled={!hayCambios}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    <form id='form_inscripcion' onSubmit={actualizar}>
                        {secciones.map((seccion, i) => (
                            <SeccionPanel
                                key={seccion.titulo}
                                id={String(i)}
                                titulo={seccion.titulo}
                                activa={seccionActiva === String(i)}
                                onFocus={() => setSeccionActiva(String(i))}
                            >
                                {seccion.campos.map(({ campo, etiqueta, ayuda, numero, min = 0, max }) => (
                                    <div className="input-field" key={campo}>
                                        <input
                                            onChange={leerDato}
                                            value={editar[campo] ?? ''}
                                            id={campo}
                                            name={campo}
                                            type={numero ? 'number' : 'text'}
                                            min={numero ? min : undefined}
                                            max={max}
                                            className="validate"
                                        />
                                        <label className='active' for={campo}>{etiqueta}</label>
                                        {ayuda && <span className='helper-text'>{ayuda}</span>}
                                    </div>
                                ))}
                            </SeccionPanel>
                        ))}
                    </form>
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa de inscripción'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Inscripción'
                    ayuda='Haz clic en una fecha o un costo para editarlo'
                    acciones={
                        <button type='button' className='boton_admin' onClick={abrirPanel}>
                            Editar fechas y costos
                        </button>
                    }
                />
            </div>
        </Fragment>
    );
}

export default Aspirantes;
